import { create } from 'zustand';

export interface TrainingOrder {
  id: string;
  order_number: string;
  order_type: string;
  status: string;
  subtotal: number;
  tax_amount: number;
  discount_amount: number;
  tip_amount: number;
  total: number;
  created_at: string;
  completed_at: string | null;
  items: TrainingOrderItem[];
}

export interface TrainingOrderItem {
  id: string;
  name: string;
  quantity: number;
  unit_price: number;
  total: number;
}

interface TrainingModeState {
  /** Whether training mode is currently active */
  isTrainingMode: boolean;
  /** Whether the guided tutorial overlay is enabled */
  isTutorialEnabled: boolean;
  /** Current step index in the tutorial (0-based) */
  tutorialStep: number;
  /** In-memory fake orders created during this training session */
  trainingOrders: TrainingOrder[];
  /** Counter for generating fake order numbers */
  orderCounter: number;

  // Actions
  enterTrainingMode: () => void;
  exitTrainingMode: () => void;
  toggleTutorial: () => void;
  setTutorialStep: (step: number) => void;
  advanceTutorial: () => void;
  addTrainingOrder: (order: TrainingOrder) => void;
  clearTrainingData: () => void;
  getNextOrderNumber: () => string;
}

/** Total number of tutorial steps */
export const TUTORIAL_STEPS = [
  {
    title: 'Search and Add a Product',
    description: 'Use the search bar or browse categories to find a product, then click it to add it to the order.',
    target: 'product-search',
  },
  {
    title: 'Adjust Quantity',
    description: 'Use the + and - buttons next to an item in the order panel to change its quantity.',
    target: 'order-panel',
  },
  {
    title: 'Apply a Discount (Optional)',
    description: 'If your store uses promotions, they will apply automatically based on the items in the cart.',
    target: 'order-totals',
  },
  {
    title: 'Choose a Payment Method',
    description: 'Click the Checkout button to open the payment dialog, then select a payment method like Cash or Card.',
    target: 'checkout-button',
  },
  {
    title: 'Complete the Sale',
    description: 'Confirm the payment to complete the test sale. In Training Mode, no real transaction is recorded.',
    target: 'payment-actions',
  },
] as const;

export const TUTORIAL_STEP_COUNT = TUTORIAL_STEPS.length;

/**
 * Zustand store for Training Mode state.
 *
 * Intentionally NOT persisted -- resets on page refresh as a safety measure.
 * This ensures training mode cannot accidentally remain active across sessions.
 */
export const useTrainingModeStore = create<TrainingModeState>((set, get) => ({
  isTrainingMode: false,
  isTutorialEnabled: false,
  tutorialStep: 0,
  trainingOrders: [],
  orderCounter: 0,

  enterTrainingMode: () =>
    set({
      isTrainingMode: true,
      isTutorialEnabled: false,
      tutorialStep: 0,
      trainingOrders: [],
      orderCounter: 0,
    }),

  exitTrainingMode: () =>
    set({
      isTrainingMode: false,
      isTutorialEnabled: false,
      tutorialStep: 0,
      trainingOrders: [],
      orderCounter: 0,
    }),

  toggleTutorial: () =>
    set((state) => ({
      isTutorialEnabled: !state.isTutorialEnabled,
      tutorialStep: 0,
    })),

  setTutorialStep: (step) => set({ tutorialStep: step }),

  advanceTutorial: () =>
    set((state) => {
      const nextStep = state.tutorialStep + 1;
      if (nextStep >= TUTORIAL_STEP_COUNT) {
        return { isTutorialEnabled: false, tutorialStep: 0 };
      }
      return { tutorialStep: nextStep };
    }),

  addTrainingOrder: (order) =>
    set((state) => ({
      trainingOrders: [...state.trainingOrders, order],
    })),

  clearTrainingData: () =>
    set({
      trainingOrders: [],
      orderCounter: 0,
    }),

  getNextOrderNumber: () => {
    const next = get().orderCounter + 1;
    set({ orderCounter: next });
    return `T-${String(next).padStart(4, '0')}`;
  },
}));
